import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Paperclip, X, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/cn"

type Props = {
  ticketId: number
  disabled?: boolean
}

// Mirrors Agents::AttachmentPolicy on the server. Kept inline rather than
// fetched because the composer doesn't have a status endpoint and these
// limits are stable.
const MAX_FILE_BYTES = 10 * 1024 * 1024
const ALLOWED_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "video/mp4",
  "application/pdf",
])
const ACCEPT_ATTR = Array.from(ALLOWED_MIME).join(",")

// Reply box at the bottom of the conversation thread. Posts to
// /tickets/:id/reply, which persists an AgentMessage with role:
// :human_agent and broadcasts on the company stream — every open thread
// across the company sees the new reply within debounce time.
//
// Files (images / mp4 / pdf, ≤10MB each) ride along on the same POST as
// multipart form data; Inertia auto-detects File entries and switches to
// FormData. Drag-and-drop and the paperclip button both feed the same
// pendingFiles[] array, with previews rendered above the textarea.
export function TicketComposer({ ticketId, disabled = false }: Props) {
  const [body, setBody]               = useState("")
  const [submitting, setSubmitting]   = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [fileError, setFileError]     = useState<string | null>(null)
  const [dragHover, setDragHover]     = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Typing-indicator throttle. Pings POST /tickets/:id/typing at most
  // once per 500ms while the operator is typing, then a `state=stop`
  // ping ~3s after typing pauses so the indicator clears on viewers.
  // The send-button handler also fires a stop ping; in the common case
  // the AgentMessage commit's broadcast strips the indicator client-side
  // before the explicit stop arrives, but the explicit stop covers the
  // "typed and gave up" case.
  const lastTypingPingAtRef = useRef(0)
  const stopTypingTimerRef  = useRef<number | null>(null)
  function pingTyping(state: "start" | "stop") {
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (csrf) headers["X-CSRF-Token"] = csrf
    fetch(`/tickets/${ticketId}/typing`, {
      method:  "POST",
      headers,
      body:    JSON.stringify({ state }),
      credentials: "same-origin",
    }).catch(() => { /* indicator is UX polish, swallow failures */ })
  }
  function noteKeystroke() {
    const now = Date.now()
    // Throttle starts to ~one per 500ms.
    if (now - lastTypingPingAtRef.current >= 500) {
      lastTypingPingAtRef.current = now
      pingTyping("start")
    }
    // Reset the stop-timer; fires once typing pauses for 3s.
    if (stopTypingTimerRef.current != null) window.clearTimeout(stopTypingTimerRef.current)
    stopTypingTimerRef.current = window.setTimeout(() => {
      lastTypingPingAtRef.current = 0
      stopTypingTimerRef.current = null
      pingTyping("stop")
    }, 3000)
  }
  // Final cleanup when the composer unmounts (operator navigated away
  // mid-typing) — emit a stop so the indicator doesn't get stuck.
  useEffect(() => {
    return () => {
      if (stopTypingTimerRef.current != null) {
        window.clearTimeout(stopTypingTimerRef.current)
        if (lastTypingPingAtRef.current > 0) pingTyping("stop")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function addFiles(incoming: File[]) {
    if (incoming.length === 0) return
    const accepted: File[] = []
    for (const f of incoming) {
      if (!ALLOWED_MIME.has(f.type)) {
        setFileError(`${f.name}: unsupported file type (${f.type || "unknown"}).`)
        continue
      }
      if (f.size > MAX_FILE_BYTES) {
        setFileError(`${f.name}: file too large (max 10MB).`)
        continue
      }
      accepted.push(f)
    }
    if (accepted.length > 0) {
      setFileError(null)
      setPendingFiles((prev) => [...prev, ...accepted])
    }
  }

  function removeFile(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  function submit() {
    if (submitting || disabled) return
    if (!body.trim() && pendingFiles.length === 0) return
    // Cancel pending stop-timer and emit an immediate stop — the
    // about-to-be-persisted message will strip the indicator anyway,
    // but explicit beats inferred.
    if (stopTypingTimerRef.current != null) {
      window.clearTimeout(stopTypingTimerRef.current)
      stopTypingTimerRef.current = null
    }
    if (lastTypingPingAtRef.current > 0) {
      lastTypingPingAtRef.current = 0
      pingTyping("stop")
    }
    setSubmitting(true)
    const payload: Record<string, string | File[]> = { body }
    if (pendingFiles.length > 0) payload.files = pendingFiles
    router.post(`/tickets/${ticketId}/reply`, payload, {
      preserveScroll: true,
      forceFormData:  pendingFiles.length > 0,
      onSuccess:  () => { setBody(""); setPendingFiles([]); setFileError(null) },
      onFinish:   () => setSubmitting(false),
    })
  }

  function send(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    submit()
  }

  function onDrop(e: React.DragEvent<HTMLFormElement>) {
    e.preventDefault()
    setDragHover(false)
    if (disabled) return
    addFiles(Array.from(e.dataTransfer.files))
  }

  return (
    <form
      onSubmit={send}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragHover(true) }}
      onDragLeave={() => setDragHover(false)}
      onDrop={onDrop}
      className={cn(
        "border-t border-border bg-app-panel p-4 transition",
        dragHover && "bg-brand-red/5",
      )}
    >
      {pendingFiles.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {pendingFiles.map((f, i) => (
            <PendingChip key={`${f.name}-${i}`} file={f} onRemove={() => removeFile(i)} />
          ))}
        </div>
      )}
      {fileError && (
        <div className="mb-2 rounded-control border border-status-error/30 bg-status-error/10 px-2 py-1 text-xs text-status-error">
          {fileError}
        </div>
      )}
      <textarea
        value={body}
        onChange={(e) => { setBody(e.target.value); noteKeystroke() }}
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter inserts a newline (matches every chat
          // composer in the portal).
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            submit()
          }
        }}
        disabled={disabled || submitting}
        placeholder={disabled ? "Conversation closed — reopen to reply." : "Reply to the customer…  (Enter to send · Shift+Enter for newline · drop files to attach)"}
        rows={3}
        className="w-full resize-none rounded-control border border-border bg-white/5 px-3 py-2 text-sm text-app-text placeholder:text-app-subtle outline-none transition focus:border-brand-red/50 disabled:opacity-50"
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []))
          e.target.value = "" // allow re-selecting the same file
        }}
      />
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs text-app-muted">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || submitting}
            className="inline-flex items-center gap-1 rounded-control border border-border bg-white/5 px-2 py-1 text-app-text hover:bg-white/10 disabled:opacity-50"
            title="Attach files (images, mp4, pdf — 10MB max each)"
          >
            <Paperclip size={13} strokeWidth={2} />
            Attach
          </button>
          <span>Replies are saved as the human agent on this convo.</span>
        </div>
        <Button
          type="submit"
          disabled={disabled || submitting || (!body.trim() && pendingFiles.length === 0)}
        >
          {submitting ? "Sending…" : "Send reply"}
        </Button>
      </div>
    </form>
  )
}

function PendingChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith("image/")
  const isVideo = file.type.startsWith("video/")
  const sizeKb  = (file.size / 1024).toFixed(1)
  const previewUrl = (isImage || isVideo) ? URL.createObjectURL(file) : null
  // Revoke the object URL when this chip unmounts (or the file changes).
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }
  }, [previewUrl])

  return (
    <div className="group relative flex items-center gap-2 rounded-control border border-border bg-app-panel2 px-2 py-1">
      {isImage && previewUrl && (
        <img src={previewUrl} alt={file.name} className="h-8 w-8 rounded-sm object-cover" />
      )}
      {isVideo && previewUrl && (
        <video src={previewUrl} className="h-8 w-8 rounded-sm object-cover" muted />
      )}
      {!isImage && !isVideo && <FileText size={14} className="text-app-muted" />}
      <span className="max-w-[180px] truncate text-xs text-app-text" title={file.name}>
        {file.name}
      </span>
      <span className="text-[10px] text-app-subtle">{sizeKb} KB</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 rounded-full p-0.5 text-app-muted hover:bg-white/10 hover:text-status-error"
        title="Remove"
        aria-label={`Remove ${file.name}`}
      >
        <X size={12} />
      </button>
    </div>
  )
}
